/**
 * Evidence readiness — Documents_On_Site Prompt 10.
 *
 * Single 0..100 score capturing whether a site is ready to generate a
 * high-quality report or whether the user would benefit from waiting
 * (uploading more docs, finishing the SED bootstrap, etc.).
 *
 * Composite score (docs-included / awaiting):
 *   - 0.40  Provider coverage   (verified SED sections / total)
 *   - 0.40  Document coverage   (checklist satisfaction averaged across
 *                                applicable report types)
 *   - 0.10  Document quality    (priority docs that didn't fall back to
 *                                a binary placeholder during text extract)
 *   - 0.10  Site profile        (boundary drawn + brief filled)
 *
 * Composite score (`ai-only` — Datacrate Engine):
 *   Document coverage + document quality are excluded (0% doc coverage
 *   is intentional). Weights renormalise to provider 0.80 + profile 0.20
 *   so the headline score stays on the same 0..100 scale.
 *
 * Tiers:
 *   - 85+      excellent
 *   - 70..84   good
 *   - 50..69   fair
 *   - <50      thin
 *
 * Pure / side-effect free so the score persists on the site doc as
 * `SiteEntity.evidenceReadiness` for fast reads on the Dashboard.
 */
import type { SiteEntity } from '../types/site';
import type { SiteDocument } from '../types/siteRag';
import type { SiteEvidenceDocument, EvidenceSection, SedSectionId } from '../types/siteEvidence';
import type { SiteContext } from '../types/siteContext';
import type { ReportTypeId } from '../config/reportTypes';
import { isLibraryIndexDoc } from './knowledge/libraryIndexDocument';
import { hasUsableExtractedText } from '../services/knowledge/extractedTextQuality';
import { matchUploadedDocsToChecklist } from '../config/documentChecklists';
import { isNonDocumentRequirement } from '../config/reportSetupRequirements';
import { SED_SECTION_IDS } from '../types/siteEvidence';
import { REPORT_TYPE_REGISTRY } from '../config/reportTypes';
import type { ImpactSeverity } from './providerImpactForIntent';
// SED-FOUNDATION — warn-level open-questions penalty folded into readiness.
import { computeOpenQuestionPenalty } from './sed/evidenceWorkflow';

export type EvidenceReadinessTier = 'excellent' | 'good' | 'fair' | 'thin';

export interface EvidenceReadiness {
  score: number;                                      // 0..100
  tier: EvidenceReadinessTier;
  providerCoverage: number;                           // 0..1
  docCoverage: Partial<Record<ReportTypeId, number>>; // 0..1 per applicable type
  criticalGaps: { reportType: ReportTypeId; missingItem: string }[];
  recommendations: string[];
  /** ISO timestamp of the computation, so a stale persistence can be detected. */
  computedAt: string;
  /**
   * SED-FOUNDATION — warn-level open-questions / unverified-claim penalty
   * folded into `score`. Always >= 0 (subtractive, capped). Absent /
   * `{ ..., penalty: 0 }` for a legacy SED with no `workflows` field, so the
   * headline score is byte-identical to pre-foundation behaviour.
   */
  workflowPenalty?: {
    openCount: number;
    unverifiedCount: number;
    /** Whole-points subtracted from the 0..100 score (rounded). */
    pointsDeducted: number;
  };
}

/* ── Evidence mode (ES3 / DAE3) ─────────────────────────────────── */

/** Canonical mode — legacy `undefined` reads as `'ai-only'`. */
export function effectiveEvidenceMode(
  evidenceMode: SiteEntity['evidenceMode'] | undefined,
): NonNullable<SiteEntity['evidenceMode']> {
  return evidenceMode ?? 'ai-only';
}

export function isAiOnlyEvidenceMode(
  evidenceMode: SiteEntity['evidenceMode'] | undefined,
): boolean {
  return effectiveEvidenceMode(evidenceMode) === 'ai-only';
}

/* ── Component computations ─────────────────────────────────────── */

/** Fraction of SED sections whose status is 'verified' or 'anomalous'. 0 when SED is null. */
function computeProviderCoverage(sed: SiteEvidenceDocument | null): number {
  if (!sed) return 0;
  let verified = 0;
  let total = 0;
  for (const id of SED_SECTION_IDS as readonly SedSectionId[]) {
    const slot = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[id];
    if (!slot) continue;
    total++;
    if (slot.status === 'verified' || slot.status === 'anomalous') verified++;
  }
  return total > 0 ? verified / total : 0;
}

/**
 * For each report type the site has produced (or feasibility as the
 * default), compute the fraction of checklist items satisfied by the
 * site's user-uploaded documents.
 */
function computeDocCoverage(
  site: SiteEntity,
  applicableTypes: ReportTypeId[],
): { coverage: Partial<Record<ReportTypeId, number>>; criticalGaps: EvidenceReadiness['criticalGaps'] } {
  const userDocs = (site.documents ?? []).filter(d => !isLibraryIndexDoc(d));
  const coverage: Partial<Record<ReportTypeId, number>> = {};
  const criticalGaps: EvidenceReadiness['criticalGaps'] = [];
  for (const t of applicableTypes) {
    const m = matchUploadedDocsToChecklist(userDocs, t);
    const satisfied = m.satisfied.filter(item => !isNonDocumentRequirement(item));
    const missing = m.missing.filter(item => !isNonDocumentRequirement(item));
    const total = satisfied.length + missing.length;
    coverage[t] = total > 0 ? satisfied.length / total : 0;
    for (const item of missing) {
      if (item.severity === 'critical') {
        criticalGaps.push({ reportType: t, missingItem: item.label });
      }
    }
  }
  return { coverage, criticalGaps };
}

/**
 * Document-quality component — fraction of priority (★) docs that
 * carry real text content (not the `[Binary file …]` / `[Image …]`
 * placeholders extractTextFromFile emits when text isn't available).
 * Returns 1.0 when there are no priority docs (penalty doesn't fire
 * if the user hasn't asked for deep analysis on anything).
 */
export function computeDocQuality(site: SiteEntity): number {
  const priorityDocs = (site.documents ?? []).filter(d => d.priority && !isLibraryIndexDoc(d));
  if (priorityDocs.length === 0) return 1;
  // DIM-1 — shared predicate (`extractedTextQuality.ts`) replaces the
  // hand-mirrored prefix list. Corrections vs the old inline guard:
  // `[Image vision: …]` docs count as usable (they carry a real vision
  // extraction) and `[PDF: … — no extractable text]` placeholders no
  // longer count as usable.
  const usable = priorityDocs.filter(d => hasUsableExtractedText(d.textContent));
  return usable.length / priorityDocs.length;
}

/**
 * Site profile component — boundary drawn + site context populated.
 *
 * DR3 — the legacy `site.description` weight is replaced with a
 * SiteContext-claim-count weight. A site with at least one claim
 * earns the same +0.4 the brief used to; richer context scales up
 * to +0.6 at five-or-more claims so users who do thorough SC entry
 * see a measurable boost.
 *
 * Back-compat: the DR1 lazy migrator lifts any legacy `description`
 * into an SC `general/brief` claim, so a site that previously had
 * "description set, SC empty" now reads as "claimCount = 1" and
 * earns the same +0.4 it did before.
 */
export function computeSiteProfile(
  site: SiteEntity,
  siteContext?: SiteContext,
): number {
  let score = 0;
  if (site.siteBoundary && site.siteBoundary.length >= 3) score += 0.6;
  const claimCount = siteContext?.claims?.length ?? site.siteContext?.claims?.length ?? 0;
  if (claimCount >= 1) {
    // 0.4 baseline at one claim, +0.05 per additional claim, capped at 0.6.
    score += Math.min(0.6, 0.4 + (claimCount - 1) * 0.05);
  }
  return score;
}

/* ── Tiers + recommendations ─────────────────────────────────────── */

export function tierFromScore(score: number): EvidenceReadinessTier {
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 50) return 'fair';
  return 'thin';
}

/* ── RV1 — gap-aware honest tier ─────────────────────────────────── */

/**
 * Honest tier vocabulary — distinct from the composite-only
 * `EvidenceReadinessTier` so call-sites that want the gap-aware label
 * pick this up explicitly. Mapped to the existing tier in
 * `honestTierFromComposite()` below.
 *
 * - `excellent` — composite ≥85 AND no significant/critical gaps.
 * - `strong`    — composite ≥70 AND no significant/critical gaps.
 * - `watch`     — composite ≥50, OR a `significant` gap is present.
 *                  Reviewer should independently verify the gating section.
 * - `risk`      — composite <50, OR a `critical` gap is present.
 *                  Report should not ship until the gap is filled.
 */
export type HonestTier = 'excellent' | 'strong' | 'watch' | 'risk';

const HONEST_TIER_ORDER: Record<HonestTier, number> = {
  excellent: 3,
  strong: 2,
  watch: 1,
  risk: 0,
};

/** Floor of the two — lower = more honest. Used by the tier-cap rule. */
export function tierMin(a: HonestTier, b: HonestTier): HonestTier {
  return HONEST_TIER_ORDER[a] <= HONEST_TIER_ORDER[b] ? a : b;
}

/** Map the existing composite-only tier to the honest vocabulary so the
 *  cap rule can compare like-for-like. */
export function honestTierFromComposite(score: number): HonestTier {
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'strong';
  if (score >= 50) return 'watch';
  return 'risk';
}

/** Per-section effective tier — the output of RV3's panel. */
export type SectionEffectiveTier = 'strong' | 'watch' | 'risk';

const SECTION_TIER_ORDER: Record<SectionEffectiveTier, number> = {
  strong: 2,
  watch: 1,
  risk: 0,
};

/** Compute the worst section's tier across the panel. Empty list →
 *  `'strong'` (no section data ⇒ no per-section drag). */
export function computeWorstSectionTier(
  sections: ReadonlyArray<{ tier: SectionEffectiveTier }>,
): SectionEffectiveTier {
  let worst: SectionEffectiveTier = 'strong';
  for (const s of sections) {
    if (SECTION_TIER_ORDER[s.tier] < SECTION_TIER_ORDER[worst]) worst = s.tier;
  }
  return worst;
}

/** Promote a SectionEffectiveTier into the honest tier space so the cap
 *  rule can compare composite-tier vs. worst-section-tier. */
function sectionTierToHonest(tier: SectionEffectiveTier): HonestTier {
  switch (tier) {
    case 'strong': return 'strong';
    case 'watch':  return 'watch';
    case 'risk':   return 'risk';
  }
}

export interface HonestTierResult {
  tier: HonestTier;
  /** Single-sentence, user-facing explanation of why this tier was
   *  chosen (e.g. "1 significant evidence gap — Water Resources"). */
  reason: string;
  /** SedSectionId of the section pulling the tier down, when the cap
   *  fires on a section. Absent when the composite or a gap is the
   *  binding constraint. */
  gatingSectionId?: string;
}

export interface HonestTierInput {
  compositeScore: number;
  /** Section-level effective tiers from RV3's per-section panel. The
   *  worst section caps the headline. Pass `[]` when the panel hasn't
   *  been computed yet (cap collapses to the composite). */
  sectionImpacts?: ReadonlyArray<{ sectionId: string; tier: SectionEffectiveTier; reason?: string }>;
  /** Effective gap severities AFTER `effectiveGapImpact` re-weighting.
   *  Each entry's `severity` is the post-RV2 value. Pass `[]` when no
   *  gaps. */
  gaps?: ReadonlyArray<{ severity: ImpactSeverity; field?: string; providerId?: string }>;
}

/**
 * RV1 — gap-aware honest tier.
 *
 * The headline tier respects three constraints:
 *   1. A `critical` gap forces `risk`, no matter the composite.
 *   2. A `significant` gap caps the headline at `watch`.
 *   3. Otherwise, the headline cannot exceed the worst section's tier
 *      (the cap rule).
 *
 * Pure / deterministic. Imported by:
 *   - RV3 SectionReadinessPanel (renders the headline pill)
 *   - DataReadinessPill (replaces the existing tier label)
 *   - Report header / verdict chip
 *   - RV-B section-level Watch banner (re-uses the gating rule)
 */
export function honestTier(input: HonestTierInput): HonestTierResult {
  const gaps = input.gaps ?? [];
  const sections = input.sectionImpacts ?? [];

  const criticalGaps    = gaps.filter(g => g.severity === 'critical');
  const significantGaps = gaps.filter(g => g.severity === 'significant');

  if (criticalGaps.length > 0) {
    const label = criticalGaps.length === 1 ? '1 critical evidence gap' : `${criticalGaps.length} critical evidence gaps`;
    return { tier: 'risk', reason: `${label} — report should not ship until filled.` };
  }

  if (significantGaps.length > 0) {
    const label = significantGaps.length === 1 ? '1 significant evidence gap' : `${significantGaps.length} significant evidence gaps`;
    const sectionHint = describeGatingGap(significantGaps[0]);
    return {
      tier: 'watch',
      reason: `${label}${sectionHint ? ` (${sectionHint})` : ''} — reviewer should independently verify the gating section.`,
      gatingSectionId: significantGaps[0]?.providerId ?? significantGaps[0]?.field,
    };
  }

  // No significant/critical gaps — apply the cap rule.
  const compositeTier = honestTierFromComposite(input.compositeScore);
  if (sections.length === 0) {
    return { tier: compositeTier, reason: '' };
  }
  const worst = computeWorstSectionTier(sections);
  // `strong` is the BEST possible SectionEffectiveTier — a panel full
  // of strong sections shouldn't cap an `excellent` composite (excellent
  // means "all sections verified AND the composite components are high").
  // The cap only fires when the worst section drops BELOW strong, which
  // is exactly when the user needs to see "the headline is watch
  // because section X is watch".
  if (worst === 'strong') {
    return { tier: compositeTier, reason: '' };
  }
  const worstHonest = sectionTierToHonest(worst);
  const capped = tierMin(compositeTier, worstHonest);
  if (capped === compositeTier) {
    return { tier: compositeTier, reason: '' };
  }
  // Cap fired — surface which section is dragging the headline down.
  const gating = sections.find(s => s.tier === worst);
  return {
    tier: capped,
    reason: gating?.reason
      ? `Section cap fired — ${gating.sectionId}: ${gating.reason}`
      : `Section cap fired — ${gating?.sectionId ?? 'a section'} is at ${worst}.`,
    gatingSectionId: gating?.sectionId,
  };
}

/** Best-effort human-readable name for the gap's gating section. Falls
 *  back to the raw provider/field identifier when no mapping is known. */
function describeGatingGap(gap: { field?: string; providerId?: string } | undefined): string | null {
  if (!gap) return null;
  const id = (gap.providerId ?? gap.field ?? '').toLowerCase();
  if (!id) return null;
  // Mirror of providerImpactForIntent's id space — keep in sync.
  if (id.includes('water')) return 'Water Resources';
  if (id.includes('grid')) return 'Grid Power';
  if (id.includes('flood')) return 'Flood';
  if (id.includes('seismic')) return 'Seismic';
  if (id.includes('air')) return 'Air Quality';
  if (id.includes('environ')) return 'Environmental';
  if (id.includes('nuclear') || id.includes('adams')) return 'Nuclear Infrastructure';
  if (id.includes('connect') || id.includes('fiber')) return 'Connectivity';
  if (id.includes('parcel')) return 'Site / Parcel';
  if (id.includes('reg')) return 'Regulatory';
  if (id.includes('elect') || id.includes('tariff')) return 'Electricity';
  if (id.includes('climate')) return 'Climate';
  if (id.includes('solar') || id.includes('wind')) return 'Renewable Resource';
  return gap.providerId ?? gap.field ?? null;
}

/**
 * Heuristic recommendations — rank-ordered by which slot, when
 * satisfied, would lift the readiness score the most. The boosts are
 * approximate (the user copy says "(+10)") so the panel renders a
 * stable, sane suggestion list without re-running the full match.
 */
function buildRecommendations(
  site: SiteEntity,
  criticalGaps: EvidenceReadiness['criticalGaps'],
  applicableTypes: ReportTypeId[],
  docCoverage: Partial<Record<ReportTypeId, number>>,
  siteContext?: SiteContext,
  options?: { suppressDocRecommendations?: boolean },
): string[] {
  const recs: string[] = [];
  const suppressDocs = options?.suppressDocRecommendations ?? false;

  if (!suppressDocs) {
    // Critical gaps first — biggest score lift.
    for (const gap of criticalGaps.slice(0, 3)) {
      recs.push(`Upload ${gap.missingItem.toLowerCase()} (+10) — strengthens ${REPORT_TYPE_REGISTRY[gap.reportType]?.shortLabel ?? gap.reportType}.`);
    }

    // Recommend the next missing doc on whichever applicable type has
    // the lowest coverage.
    const ranked = applicableTypes
      .map(t => ({ t, c: docCoverage[t] ?? 0 }))
      .sort((a, b) => a.c - b.c);
    for (const { t } of ranked.slice(0, 2)) {
      const m = matchUploadedDocsToChecklist((site.documents ?? []).filter(d => !isLibraryIndexDoc(d)), t);
      const recommended = m.missing.find(i => i.severity === 'recommended' && !isNonDocumentRequirement(i));
      if (recommended && !recs.some(r => r.toLowerCase().includes(recommended.label.toLowerCase()))) {
        recs.push(`Upload ${recommended.label.toLowerCase()} (+5) — improves ${REPORT_TYPE_REGISTRY[t]?.shortLabel ?? t} confidence.`);
      }
    }
  }

  if (!site.siteBoundary || site.siteBoundary.length < 3) {
    recs.push('Draw a site boundary (+6) — unlocks the site profile evidence section.');
  }
  // DR3 — copy updated to mention SiteContext, not the legacy brief.
  const claimCount = siteContext?.claims?.length ?? site.siteContext?.claims?.length ?? 0;
  if (claimCount === 0) {
    recs.push('Add what you know about this site (+4 to +6) — site context anchors every report.');
  }

  return recs.slice(0, 5);
}

/* ── Public entry point ─────────────────────────────────────────── */

/**
 * Compute evidence readiness for a site. Pure: idempotent for a given
 * (site, sed, siteContext) tuple so callers can persist + cache
 * without re-reading.
 *
 * `applicableReportTypes` overrides the default detection (every type
 * the site has produced, falling back to feasibility for new sites)
 * and is exported for tests.
 *
 * DR3 — gains an optional `siteContext` parameter so the site-profile
 * component can score claim count instead of the retired
 * `site.description` field. When omitted, falls back to
 * `site.siteContext` on the entity itself (the typical path).
 */
export function computeEvidenceReadiness(
  site: SiteEntity,
  sed: SiteEvidenceDocument | null,
  applicableReportTypes?: ReportTypeId[],
  siteContext?: SiteContext,
): EvidenceReadiness {
  const applicableTypes = applicableReportTypes && applicableReportTypes.length > 0
    ? applicableReportTypes
    : (['feasibility'] as ReportTypeId[]);

  const effectiveContext = siteContext ?? site.siteContext;
  const aiOnly = isAiOnlyEvidenceMode(site.evidenceMode);
  const providerCoverage = computeProviderCoverage(sed);
  const { coverage, criticalGaps: rawCriticalGaps } = computeDocCoverage(site, applicableTypes);
  const criticalGaps = aiOnly ? [] : rawCriticalGaps;
  const docCoverage = aiOnly ? {} : coverage;
  const avgDoc = aiOnly
    ? 0
    : (applicableTypes.length > 0
      ? applicableTypes.reduce((sum, t) => sum + (coverage[t] ?? 0), 0) / applicableTypes.length
      : 0);
  const docQuality = computeDocQuality(site);
  const siteProfile = computeSiteProfile(site, effectiveContext);

  const composite = aiOnly
    ? providerCoverage * 0.8 + siteProfile * 0.2
    : providerCoverage * 0.4
      + avgDoc * 0.4
      + docQuality * 0.1
      + siteProfile * 0.1;

  // SED-FOUNDATION — warn-level workflow term. Subtractive + capped: it
  // lowers the headline (open questions + confirmed-without-evidence claims)
  // but NEVER zeroes / blocks it. Legacy SEDs (no `workflows`) yield a zero
  // penalty so `score` is byte-identical to pre-foundation behaviour.
  const workflowPenalty = computeOpenQuestionPenalty({ workflows: sed?.workflows });

  const baseScore = Math.round(composite * 100);
  const pointsDeducted = Math.round(workflowPenalty.penalty * 100);
  const score = baseScore - pointsDeducted;
  const clamped = Math.max(0, Math.min(100, score));

  return {
    score: clamped,
    tier: tierFromScore(clamped),
    providerCoverage,
    docCoverage,
    criticalGaps,
    recommendations: buildRecommendations(
      site,
      criticalGaps,
      applicableTypes,
      docCoverage,
      effectiveContext,
      { suppressDocRecommendations: aiOnly },
    ),
    computedAt: new Date().toISOString(),
    workflowPenalty: {
      openCount: workflowPenalty.openCount,
      unverifiedCount: workflowPenalty.unverifiedCount,
      pointsDeducted,
    },
  };
}

/* ── Dashboard sort helper ──────────────────────────────────────── */

/**
 * Comparator for sorting sites by readiness desc (best first), then
 * by name as a tiebreaker. Sites without a persisted readiness sort
 * to the bottom so the Dashboard surfaces "ready" sites at the top.
 */
export function compareSitesByReadiness(a: SiteEntity, b: SiteEntity): number {
  const sa = a.evidenceReadiness?.score ?? -1;
  const sb = b.evidenceReadiness?.score ?? -1;
  if (sa !== sb) return sb - sa;
  return a.name.localeCompare(b.name);
}
